/**
 * Captions toggle for theme videos.
 *
 * Any <video> carrying a captions <track> gets a visible CC control so the captions can be
 * turned on. Videos rendered with native `controls` already expose the browser's own captions
 * menu, so they are left alone.
 *
 * Captions start hidden to keep each video's base visual state unchanged; the toggle is the
 * opt-in. The chosen state is remembered for the session so a viewer who turns captions on
 * does not have to repeat it on every video.
 */
(function () {
  var STORAGE_KEY = 'yise:captions-on';
  var WRAPPER_CLASS = 'video-captions__wrap';

  function prefersCaptions() {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function rememberPreference(on) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch (e) {
      /* Private mode: the toggle still works, it just does not persist. */
    }
  }

  function captionTracks(video) {
    var found = [];
    for (var i = 0; i < video.textTracks.length; i++) {
      var track = video.textTracks[i];
      if (track.kind === 'captions' || track.kind === 'subtitles') found.push(track);
    }
    return found;
  }

  function applyState(video, on) {
    captionTracks(video).forEach(function (track) {
      track.mode = on ? 'showing' : 'hidden';
    });
  }

  function positionContext(video) {
    var parent = video.parentElement;
    if (!parent) return null;
    if (parent.classList.contains(WRAPPER_CLASS)) return parent;

    var position = window.getComputedStyle(parent).position;
    if (position === 'static') parent.classList.add('video-captions__anchor');
    return parent;
  }

  function build(video) {
    if (video.dataset.captionsToggle === 'ready') return;
    if (!video.querySelector('track[kind="captions"], track[kind="subtitles"]')) return;
    video.dataset.captionsToggle = 'ready';

    // Native controls already carry a captions menu.
    if (video.hasAttribute('controls')) {
      if (prefersCaptions()) applyState(video, true);
      return;
    }

    var host = positionContext(video);
    if (!host) return;

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'video-captions-toggle';
    button.setAttribute('aria-pressed', 'false');
    button.innerHTML =
      '<span aria-hidden="true">CC</span><span class="visually-hidden">CC captions</span>';

    button.addEventListener('click', function () {
      var on = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      applyState(video, on);
      rememberPreference(on);
    });

    host.appendChild(button);

    // Several sections switch native controls on once playback starts. The browser's own
    // captions menu takes over at that point, so the standalone toggle steps aside rather
    // than duplicating it and colliding with the control bar.
    new MutationObserver(function () {
      button.hidden = video.hasAttribute('controls');
    }).observe(video, { attributes: true, attributeFilter: ['controls'] });

    if (prefersCaptions()) {
      button.setAttribute('aria-pressed', 'true');
      applyState(video, true);
    } else {
      applyState(video, false);
    }
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.nodeType === 1 && root.matches && root.matches('video')) build(root);
    root.querySelectorAll('video').forEach(build);
  }

  function start() {
    scan(document);
    // Sections, Swiper clones, and dynamic components insert videos after first paint.
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(scan);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
